import { useState, useCallback } from 'react';
import { supabase } from '@/clients/supabase';
import { useToast } from '@/shared/contexts/ToastContext';
import { getAuthRedirectUrl, logEnvironmentInfo } from '../utils/environment.utils';

interface UsePasswordResetReturn {
  sendResetEmail: (email: string) => Promise<boolean>;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
  getStoredResetInfo: () => { email: string | null; timestamp: number | null };
  clearStoredResetInfo: () => void;
}

export const usePasswordReset = (): UsePasswordResetReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();

  const sendResetEmail = useCallback(async (email: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      // Get proper redirect URL for current environment
      const redirectUrl = getAuthRedirectUrl('/auth/confirm');
      
      // Debug environment info
      logEnvironmentInfo();
      console.log('🔄 Password reset redirect URL:', redirectUrl);

      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (resetError) {
        let errorMessage = resetError.message;
        
        // Handle specific error cases with user-friendly messages
        if (resetError.message.includes('rate limit')) {
          errorMessage = 'Terlalu banyak permintaan reset. Silakan tunggu beberapa menit sebelum mencoba lagi.';
        } else if (resetError.message.includes('not found')) {
          errorMessage = 'Email tidak ditemukan dalam sistem kami.';
        } else if (resetError.message.includes('signup')) {
          errorMessage = 'Email belum terdaftar. Silakan daftar terlebih dahulu.';
        }

        setError(errorMessage);
        showToast({ type: 'error', message: errorMessage });
        return false;
      }

      // Store reset info for recovery purposes
      localStorage.setItem('reset_password_email', email);
      localStorage.setItem('reset_password_timestamp', Date.now().toString());
      
      showToast({ 
        type: 'success', 
        message: 'Link reset password telah dikirim ke email Anda. Link berlaku selama 1 jam dan hanya bisa digunakan sekali.',
        duration: 6000
      });
      
      return true;
    } catch (err) {
      const errorMessage = 'Gagal mengirim email reset. Silakan coba lagi.';
      setError(errorMessage);
      showToast({ type: 'error', message: errorMessage });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const getStoredResetInfo = useCallback(() => {
    const email = localStorage.getItem('reset_password_email');
    const timestampStr = localStorage.getItem('reset_password_timestamp');
    const timestamp = timestampStr ? parseInt(timestampStr) : null;
    
    return { email, timestamp };
  }, []);

  const clearStoredResetInfo = useCallback(() => {
    localStorage.removeItem('reset_password_email');
    localStorage.removeItem('reset_password_timestamp');
  }, []);

  return {
    sendResetEmail,
    isLoading,
    error,
    clearError,
    getStoredResetInfo,
    clearStoredResetInfo,
  };
};